/**
 * Informe del Recorregut de Vigilància — orquestación de datos (2026-07-27).
 *
 * Compone `SurveillanceReportData` para `SurveillanceReport.tsx` según las
 * opciones elegidas en el modal:
 *   1. Query propia del informe (`GET_SURVEILLANCE_ROUTE_FOR_REPORT`): sin
 *      `gpsPoints` y con los vínculos de cada tramo.
 *   2. Order gemela → ROID canónico, descripción, notas, fecha fin, operario
 *      que registra, fotos propias e hijas.
 *   3. Nombres de concesión/carretera/equipo y tripulación vía
 *      `resolveHeaderData` — el MISMO resolutor que la cabecera del detalle.
 *   4. Filas de tramo y agregados en `surveillanceReportRows.ts` (puro).
 *   5. Hijas SOLO si se piden, con el coste del nivel elegido: `list` y
 *      `list-photos` NO llaman a `fetchActionRecord` (~10 queries por
 *      actuación) — solo Order por id, más las imágenes en el nivel con fotos.
 *
 * Separado de `fetchReportData.ts` (Action/Accident) porque la shape del RV es
 * distinta: tramos como tabla propia, sin partidas/materiales en el padre, con
 * concesión/equipo/carretera resueltos.
 */

import i18n from '../../i18n';
import { client } from '../../gql/client';
import { fetchExternalWorksBySegment } from '../../gql/surveillanceRouteExternalWorks';
import { GET_SURVEILLANCE_ROUTE_FOR_REPORT } from '../../gql/queries/surveillanceRoute';
import { GET_ORDER_BY_ID } from '../../gql/queries/orders/orders';
import { OrderType } from '../../types/orders';
import { isWorkState } from '../../constants/workStates';
import {
  buildRoidFallback,
  formatRoadsWithRanges,
  mapRouteStateToTagVariant,
  formatDuration,
  resolveRouteRoadRanges,
} from '../surveillanceRoute';
import { resolveHeaderData } from '../../pages/main/surveillanceRoute/RouteDetailed/headerData';
import {
  CHUNK_SIZE,
  safeQuery,
  formatRoid,
  formatDate,
  translateState,
  fetchImages,
  fetchActionRecord,
  fetchAccidentRecord,
  fetchCommunicationRecord,
  resolveRegisteredByName,
  resolveClassificationFields,
} from './fetchReportData';
import {
  buildSegmentRows,
  buildCauseSummary,
  sumSegmentDurationMs,
} from './surveillanceReportRows';
import {
  LinkedOrderReportBlock,
  SurveillanceReportData,
  SurveillanceReportOptions,
} from './surveillanceTypes';
import {
  splitLinkedWorks,
  firstCommunicationOf,
  linkedOrderInfoRows,
} from './surveillanceReportLinked';
import {
  ReportSortOption,
  DEFAULT_REPORT_SORT,
  buildReportChildrenSequence,
  sortReportChildren,
} from './combinedSort';
import { DetailedPDFRecord } from './detailedTypes';
import { getCachedRoadMeshes } from '../../hooks/usePrecacheRouteMesh';
import { withTimeout } from '../withTimeout';

const EMPTY = '—';

/**
 * Lo que el informe espera a los trabajos del aviso DESPUÉS de tener el
 * recorrido. Salen en paralelo con la principal, así que casi siempre ya han
 * contestado; 5 s cubre la consulta lenta pero viva medida con mala cobertura
 * (hasta 4,4 s, ver `withTimeout`). Pasado el tope, el PDF sale con solo la OT.
 */
export const REPORT_WORKS_TIMEOUT_MS = 5_000;

const t = (
  key: string,
  fallback?: string,
  values?: Record<string, string>,
): string =>
  i18n.t(key, { defaultValue: fallback ?? key, ...values }) as string;

const formatPk = (km?: number | null, meter?: number | null): string =>
  km == null || meter == null
    ? EMPTY
    : `${km}+${String(meter).padStart(3, '0')}`;

function formatGeneratedAt(): string {
  const localeMap: Record<string, string> = {
    ca: 'ca-ES',
    es: 'es-ES',
    en: 'en-GB',
  };
  const dateLocale = localeMap[i18n.language] || 'ca-ES';
  return new Date().toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Hijas en el nivel LIGERO (`list` / `list-photos`): una Order por id,
 * filtrando a Actuacions — un RV solo admite `Action` y `RouteSegment` (D0),
 * pero el filtro protege de debris y de que la jerarquía cambie.
 *
 * Solo las que son trabajo (decisión D3, 2026-10-12): una básica en borrador o
 * anulada no tiene fila ni cuenta en la portada, igual que en las OTs
 * vinculadas (`splitLinkedWorks`). Sin ninguna línea que lo diga.
 */
async function fetchLightChildren(
  childOrderIds: string[],
  withPhotos: boolean,
  cloudName: string,
  segmentByOrderId: Map<string, number>,
  onProgress?: (current: number, total: number) => void,
): Promise<{
  rows: SurveillanceReportData['childRows'];
  photos: SurveillanceReportData['childPhotos'];
}> {
  const rows: SurveillanceReportData['childRows'] = [];
  const photos: SurveillanceReportData['childPhotos'] = [];
  let completed = 0;

  for (let i = 0; i < childOrderIds.length; i += CHUNK_SIZE) {
    const chunk = childOrderIds.slice(i, i + CHUNK_SIZE);
    const resolved = await Promise.all(
      chunk.map(async (childId) => {
        const res = await safeQuery<{ order: any }>(
          GET_ORDER_BY_ID,
          { id: childId },
          'network-only',
        );
        const order = res?.order;
        completed++;
        onProgress?.(completed, childOrderIds.length);
        if (
          !order ||
          order.type !== OrderType.Action ||
          !isWorkState(order.state)
        ) {
          return null;
        }
        const images = withPhotos
          ? await fetchImages(order.imageIDs ?? [], cloudName)
          : [];
        return { order, images };
      }),
    );

    resolved.forEach((entry) => {
      if (!entry) return;
      const { order, images } = entry;
      const roid = formatRoid(order.roid);
      const segmentIndex = segmentByOrderId.get(order.id);
      rows.push({
        roid,
        time: formatDate(order.registerInitDate),
        segment: segmentIndex != null ? String(segmentIndex) : '-',
        state: translateState(order.state ?? ''),
        // La validada o, si no hay, la del operario (spec 2026-10-28): el
        // informe se entrega fuera y no enseña el texto que gestión corrigió.
        description: order.descriptionValidated || order.description || '',
      });
      if (withPhotos && images.length > 0) {
        photos.push({
          title:
            segmentIndex != null
              ? `${t('modals:surveillanceReport.segmentsTitle')} ${segmentIndex} · ${roid}`
              : roid,
          images,
        });
      }
    });
  }

  return { rows, photos };
}

/**
 * Hijas en el nivel COMPLETO: una página de detalle por actuación que sea
 * trabajo (decisión D3). La básica en borrador o anulada se salta ANTES de
 * `fetchActionRecord` (~10 consultas).
 */
async function fetchFullChildren(
  childOrderIds: string[],
  cloudName: string,
  sort: ReportSortOption,
  onProgress?: (current: number, total: number) => void,
): Promise<DetailedPDFRecord[]> {
  const children: {
    record: DetailedPDFRecord;
    registerInitDate?: string | null;
    creationDate?: string | null;
  }[] = [];
  let completed = 0;

  for (let i = 0; i < childOrderIds.length; i += CHUNK_SIZE) {
    const chunk = childOrderIds.slice(i, i + CHUNK_SIZE);
    const resolved = await Promise.all(
      chunk.map(async (childId) => {
        const orderRes = await safeQuery<{ order: any }>(
          GET_ORDER_BY_ID,
          { id: childId },
          'network-only',
        );
        const order = orderRes?.order;
        if (
          !order ||
          order.type !== OrderType.Action ||
          !isWorkState(order.state)
        ) {
          completed++;
          onProgress?.(completed, childOrderIds.length);
          return null;
        }
        const record = await fetchActionRecord(childId, cloudName);
        completed++;
        onProgress?.(completed, childOrderIds.length);
        return record
          ? {
              record,
              registerInitDate: order.registerInitDate ?? null,
              creationDate: order.creationDate ?? null,
            }
          : null;
      }),
    );
    resolved.forEach((child) => {
      if (child) children.push(child);
    });
  }

  return buildReportChildrenSequence(children, sort.field, sort.direction);
}

/**
 * Anexo de fotos por tramo (2026-09-22): las fotos cuelgan de la Order gemela
 * del tramo. Una consulta por tramo + una por imagen; solo los tramos con
 * fotos aparecen. Los tramos se recorren en el MISMO orden cronológico que
 * las filas de la tabla, así el título lleva el mismo «Nº».
 */
async function fetchSegmentPhotos(
  segments: Array<{
    orderIDs?: string[] | null;
    startDate?: string | null;
    endDate?: string | null;
  }>,
  rows: SurveillanceReportData['segments'],
  cloudName: string,
): Promise<SurveillanceReportData['segmentPhotos']> {
  const ordered = [...segments].sort(
    (a, b) =>
      new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime(),
  );
  const groups: SurveillanceReportData['segmentPhotos'] = [];
  for (let i = 0; i < ordered.length; i += CHUNK_SIZE) {
    const chunk = ordered.slice(i, i + CHUNK_SIZE);
    const resolved = await Promise.all(
      chunk.map(async (segment, offset) => {
        const orderId = segment.orderIDs?.[0];
        if (!orderId) return null;
        const res = await safeQuery<{ order: any }>(GET_ORDER_BY_ID, { id: orderId }, 'network-only');
        const imageIds: string[] = res?.order?.imageIDs ?? [];
        if (imageIds.length === 0) return null;
        const images = await fetchImages(imageIds, cloudName);
        if (images.length === 0) return null;
        const row = rows[i + offset];
        const title = row
          ? `${t('pages:surveillance.segment')} ${row.index} · ${row.direction} · ${row.pkInit} – ${row.pkEnd} · ${row.startTime}–${row.endTime}`
          : `${t('pages:surveillance.segment')} ${i + offset + 1}`;
        return { title, images };
      }),
    );
    resolved.forEach((g) => g && groups.push(g));
  }
  return groups;
}

/**
 * Bloques de las OTs vinculadas al recorrido (cross-link 3, 2026-09-22):
 * ficha, primera comunicación y los trabajos de la patrulla en su turno
 * (`workOrderIds` del back, la misma atribución que la pill de la card —
 * spec 2026-09-23 § 7), en el nivel pedido, y cuántos trabajos de la OT no lo
 * son.
 */
async function fetchLinkedOrdersBlocks(
  route: any,
  mode: SurveillanceReportOptions['linkedOrders'],
  sort: ReportSortOption,
  cloudName: string,
  onProgress?: (current: number, total: number) => void,
  tableNumberById?: Map<string, number>,
): Promise<LinkedOrderReportBlock[]> {
  const links: any[] = route?.linkedExternalOrders ?? [];
  if (mode === 'none' || links.length === 0) return [];
  // Todo va a red ('network-only'): el informe es un DOCUMENTO y la caché
  // persistida de Apollo puede guardar la OT con la lista de hijas de hace
  // horas — medido en QA (22/09): la OT tenía 13 hijas en el servidor y 7 en
  // caché, y el PDF decía «0 trabajos». Misma política en el resto de lecturas
  // de Order de este informe.
  const blocks: LinkedOrderReportBlock[] = [];
  let completed = 0;

  for (const link of links) {
    const otId: string = link.order?.id;
    const otRes = await safeQuery<{ order: any }>(GET_ORDER_BY_ID, { id: otId }, 'network-only');
    const ot = otRes?.order;
    if (!ot) continue;
    const childIds: string[] = ot.childExpeditionOrderIDs ?? [];
    const children: any[] = [];
    for (let i = 0; i < childIds.length; i += CHUNK_SIZE) {
      const chunk = childIds.slice(i, i + CHUNK_SIZE);
      const resolved = await Promise.all(
        chunk.map(async (id) => (await safeQuery<{ order: any }>(GET_ORDER_BY_ID, { id }, 'network-only'))?.order ?? null),
      );
      resolved.forEach((c) => c && children.push(c));
    }
    // Misma ordenación que el resto del informe (la fecha que eligió el usuario
    // en el modal): sin esto los trabajos salían en orden de creación, que no
    // es cronológico cuando una actuación se registra con fecha anterior.
    const { kept: attributed, otherCount } = splitLinkedWorks(
      children,
      link.workOrderIds ?? [],
    );
    const kept = sortReportChildren(attributed, sort.field, sort.direction);
    const firstComm = firstCommunicationOf(children);

    const info = linkedOrderInfoRows(
      ot,
      link.segments ?? [],
      t,
      translateState,
      formatRoid,
      tableNumberById,
    );

    const workRows: LinkedOrderReportBlock['workRows'] = [];
    const workPhotos: LinkedOrderReportBlock['workPhotos'] = [];
    const workRecords: LinkedOrderReportBlock['workRecords'] = [];
    for (const child of kept) {
      const roid = formatRoid(child.roid);
      if (mode === 'full') {
        const record =
          child.type === 'accident'
            ? await fetchAccidentRecord(child.id, cloudName)
            : await fetchActionRecord(child.id, cloudName);
        if (record) workRecords.push(record);
      } else {
        workRows.push({
          roid,
          type: t(`common:orderTypes.${child.type}`),
          time: formatDate(child.registerInitDate),
          state: translateState(child.state ?? ''),
          registeredBy: await resolveRegisteredByName(child.registeredByUserId),
          // Misma regla que las básicas del recorrido; un accidente no tiene
          // validada y sale con la suya.
          description: child.descriptionValidated || child.description || '',
        });
        if (mode === 'list-photos') {
          const images = await fetchImages(child.imageIDs ?? [], cloudName);
          if (images.length > 0) workPhotos.push({ title: roid, images });
        }
      }
    }

    const firstCommunication = firstComm
      ? await fetchCommunicationRecord(firstComm.id)
      : null;
    if (firstCommunication) {
      firstCommunication.images = await fetchImages(firstComm.imageIDs ?? [], cloudName);
    }

    blocks.push({
      orderId: otId,
      roid: formatRoid(ot.roid),
      title: `${t('modals:surveillanceReport.linkedOrdersTitle')} · ${formatRoid(ot.roid)}`,
      info,
      firstCommunication,
      workRows,
      workPhotos,
      workRecords,
      otherWorksCount: otherCount,
    });
    completed++;
    onProgress?.(completed, links.length);
  }
  return blocks;
}

export async function fetchSurveillanceReportData(params: {
  routeId: string;
  options: SurveillanceReportOptions;
  cloudName: string;
  sort?: ReportSortOption;
  onProgress?: (current: number, total: number) => void;
}): Promise<SurveillanceReportData | null> {
  const { routeId, options, cloudName, sort, onProgress } = params;

  // 1. Query principal del informe (sin gpsPoints, tramos con vínculos).
  // Los trabajos que atendieron cada aviso van en paralelo (nunca rechaza: si
  // falla o no llega a tiempo, el tramo sale solo con su OT, sin «De la OT»).
  const worksPromise = fetchExternalWorksBySegment(routeId);
  let route: any;
  try {
    const { data } = await client.query({
      query: GET_SURVEILLANCE_ROUTE_FOR_REPORT,
      variables: { id: routeId },
      fetchPolicy: 'network-only',
    });
    route = data?.surveillanceRoute;
  } catch {
    return null;
  }
  if (!route) return null;
  // «No se sabe» (null) y pasado el tope valen lo mismo: sin trabajos.
  const worksInTime = withTimeout(worksPromise, REPORT_WORKS_TIMEOUT_MS);
  const externalWorks = (await worksInTime.catch(() => null)) ?? {};

  // 2. Order gemela: ROID canónico, descripción, notas, fecha fin, hijas.
  const parentOrderId: string = route.orderIDs?.[0] ?? '';
  const orderRes = parentOrderId
    ? await safeQuery<{ order: any }>(GET_ORDER_BY_ID, { id: parentOrderId }, 'network-only')
    : null;
  const order = orderRes?.order;

  // 3. Nombres + tripulación con el MISMO resolutor que la cabecera del
  //    detalle (tolerante a fallos: un nombre que no resuelve queda vacío,
  //    nunca tumba el informe).
  // Sin "Registrado por": ese dato solo se rellena al crear una Actuación o un
  // Accidente, así que en un Recorregut nunca existe. La línea era inerte.
  const [headerData, classificationFields, parentImages] = await Promise.all([
    resolveHeaderData(route).catch(() => null),
    resolveClassificationFields(route),
    fetchImages(order?.imageIDs ?? [], cloudName),
  ]);

  // 4. Tramos (puro) + agregados.
  // Multi-carretera (2026-08-15): con más de una carretera, cada fila dice la
  // suya — el PK por sí solo no identifica un sitio.
  // Las mallas ya descargadas: sin ellas el kilometraje del informe se
  // calcularía restando etiquetas de PK, y con un kilómetro largo por medio eso
  // miente (2026-08-26). La pantalla usa las mismas.
  const meshesByRoadId = getCachedRoadMeshes(
    Object.keys(headerData?.roadNamesById ?? {}),
  );
  // El sufijo del ROID de los tramos es el de la clasificación del recorrido
  // (`Su.0003836.0000.02` → `02`), que heredan al crearse y al transferirse.
  const classificationSuffix = /\.(\d{2})$/.exec(order?.roid ?? '')?.[1];
  const segments = buildSegmentRows(
    (route.segments ?? []).map((segment: { id: string }) => ({
      ...segment,
      linkedExternalWorks: externalWorks[segment.id] ?? [],
    })),
    t,
    headerData?.roadNamesById,
    meshesByRoadId,
    classificationSuffix,
  );
  // Nº de cada tramo en la tabla (mismo orden cronológico que sus filas): el
  // bloque de OT vinculada lo cita así, no por su número interno (D5).
  const tableNumberById = new Map<string, number>(
    [...((route.segments ?? []) as Array<{ id: string; startDate?: string | null }>)]
      .sort(
        (a, b) =>
          new Date(a.startDate ?? 0).getTime() -
          new Date(b.startDate ?? 0).getTime(),
      )
      .map((segment, i) => [segment.id, i + 1]),
  );
  const causeSummary = options.causeSummary
    ? buildCauseSummary(segments, t)
    : [];
  const totalElapsed = formatDuration(sumSegmentDurationMs(segments));

  // "Fecha inicio" real de la patrulla = arranque del PRIMER tramo (mismo
  // criterio que la card del detalle, 2026-07-31). NO se usa `registerInitDate`
  // porque ya no se sobrescribe al arrancar (se queda = creación) → mostrarlo
  // como "inicio" era redundante con la fila "Fecha de creación". Sin tramos
  // (RV `open`) → "Sin iniciar".
  const firstSegmentStart: string | null = ((route.segments ?? []) as Array<{
    startDate?: string | null;
  }>)
    .map((s) => s.startDate)
    .filter((d): d is string => Boolean(d))
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;

  const roid = order?.roid
    ? formatRoid(order.roid)
    : buildRoidFallback(route) || route.id;
  const stateVariant = mapRouteStateToTagVariant(route.state);
  const hasName = Boolean(route.name);

  const isMultiRoad = (route.roadIDs?.length ?? 0) > 1;

  const basicInfo: { label: string; value: string }[] = [
    { label: 'ROID', value: roid },
    { label: t('tables:headers.status'), value: translateState(stateVariant) },
    ...(hasName
      ? [{ label: t('pages:surveillance.name'), value: route.name }]
      : []),
    {
      label: t('pages:surveillance.concession'),
      value: headerData?.concessionName || EMPTY,
    },
    {
      label: t('pages:surveillance.road'),
      // Multi-carretera (2026-08-17): mismo criterio que la cabecera del
      // detalle — con VARIAS, cada rango va pegado a su carretera y las dos
      // filas sueltas de PK desaparecen. El par escalar es de la PRIMERA, y
      // enseñarlo aquí como «PK del recorrido» era mentir: los PK de una
      // troncal y su ramal no son comparables.
      value:
        formatRoadsWithRanges(
          route.roadIDs,
          headerData?.roadNamesById ?? {},
          resolveRouteRoadRanges(route)
        ) ||
        headerData?.roadName ||
        EMPTY,
    },
    { label: t('tables:headers.team'), value: headerData?.teamName || EMPTY },
    ...(isMultiRoad
      ? []
      : [
          {
            label: t('common:tables.headers.pkInit'),
            value: formatPk(route.pkInitKm, route.pkInitMeter),
          },
          {
            label: t('common:tables.headers.pkEnd'),
            value: formatPk(route.pkEndKm, route.pkEndMeter),
          },
        ]),
    {
      label: t('common:tables.headers.creationDate'),
      value: formatDate(route.creationDate),
    },
    {
      label: t('pages:surveillance.startDate'),
      value: firstSegmentStart
        ? formatDate(firstSegmentStart)
        : t('pages:surveillance.timerNotStarted'),
    },
    ...(order?.registerEndDate
      ? [
          {
            label: t('forms:workOrder.endDate'),
            value: formatDate(order.registerEndDate),
          },
        ]
      : []),
    {
      label: t('modals:surveillanceReport.segmentsTitle'),
      value: String(segments.length),
    },
    { label: t('modals:surveillanceReport.totalElapsed'), value: totalElapsed },
    // Capítol/subcapítol: el wizard del RV no los pide hoy, pero el backend los
    // acepta y los wizards hermanos (OT de sala, actuación) sí los rellenan →
    // se pintan SI existen, sin ser una opción del modal.
    ...classificationFields,
    ...(route.forceClosedAt
      ? [
          {
            label: t('pages:surveillance.forceClose.button'),
            value: formatDate(route.forceClosedAt),
          },
        ]
      : []),
  ];

  // Tripulación: operarios + vehículos con su conductor. Se emparejan por
  // posición y el más largo manda — un operario sin vehículo (o un vehículo
  // sin operario en la lista) ocupa su fila igual; el informe no puede
  // perderlos.
  const operators = headerData?.operators ?? [];
  const vehicles = headerData?.vehicles ?? [];
  const crew: SurveillanceReportData['crew'] = [];
  const crewRows = Math.max(operators.length, vehicles.length);
  for (let i = 0; i < crewRows; i++) {
    crew.push({
      operator: operators[i]?.fullName ?? '',
      vehicle: vehicles[i]?.plate ?? '',
      driver: vehicles[i]?.driverName ?? '',
    });
  }

  // 5. Hijas según el nivel pedido.
  const childOrderIds: string[] = order?.childExpeditionOrderIDs ?? [];
  const segmentByOrderId = new Map<string, number>();
  segments.forEach((row) => {
    if (row.linkedBasicActionOrderId) {
      segmentByOrderId.set(row.linkedBasicActionOrderId, row.index);
    }
  });

  let childRows: SurveillanceReportData['childRows'] = [];
  let childPhotos: SurveillanceReportData['childPhotos'] = [];
  let childRecords: DetailedPDFRecord[] = [];

  if (options.children === 'list' || options.children === 'list-photos') {
    const light = await fetchLightChildren(
      childOrderIds,
      options.children === 'list-photos',
      cloudName,
      segmentByOrderId,
      onProgress,
    );
    childRows = light.rows;
    childPhotos = light.photos;
  } else if (options.children === 'full') {
    childRecords = await fetchFullChildren(
      childOrderIds,
      cloudName,
      sort ?? DEFAULT_REPORT_SORT,
      onProgress,
    );
  }

  // 6. Fotos de los tramos y OTs vinculadas (cross-link 3), si se piden.
  const segmentPhotos = options.segmentPhotos
    ? await fetchSegmentPhotos(route.segments ?? [], segments, cloudName)
    : [];
  const linkedOrders = await fetchLinkedOrdersBlocks(
    route,
    options.linkedOrders,
    sort ?? DEFAULT_REPORT_SORT,
    cloudName,
    onProgress,
    tableNumberById,
  );

  return {
    roid,
    parentOrderId,
    state: stateVariant,
    name: hasName ? route.name : '',
    title: `${t('modals:surveillanceReport.title')} ${roid}`,
    generatedAt: formatGeneratedAt(),
    basicInfo,
    description: order?.description ?? '',
    notes: order?.notes ?? undefined,
    images: parentImages,
    crew,
    segments,
    causeSummary,
    childRows,
    childPhotos,
    childRecords,
    segmentPhotos,
    linkedOrders,
    counts: {
      segments: segments.length,
      actions: childRows.length || childRecords.length,
      linkedOrders: linkedOrders.length,
      linkedWorks: linkedOrders.reduce(
        (acc, b) => acc + b.workRows.length + b.workRecords.length,
        0,
      ),
      segmentPhotos: segmentPhotos.length,
    },
    options,
  };
}
